import React, { useCallback, useEffect, useRef, useState } from "react";
import { categoryMeta, courseCategories } from "../../data/navigation";
import "./SiteHeader.css";

const LOGO = "https://logicmojo.com/assets/dist/new_index_design/images/logo.png";
const BROCHURE_URL = "https://drive.google.com/file/d/1SzaTFL3QiyuRzbtjI7e0dzMDCnLxtr0A/view";
const CALENDLY_URL = "https://calendly.com/logicmojo/schedule-call-back-from-experts-for-live-classes?month=2026-06";
const WHATSAPP_URL = "https://api.whatsapp.com/send?phone=+918088975867&text=Hi%20Logicmojo";
const LOGIN_URL = "https://logicmojo.com/user";

const NAV_LINKS = [
  { label: "Syllabus", href: "#Syllabus" },
  { label: "Demo Session", href: "#Demo" },
  { label: "Fees", href: "#Fee" },
  { label: "Reviews", href: "#Review" },
  { label: "Contact Us", href: "#Contact" },
];

const CATEGORIES = Object.keys(courseCategories);

const Chevron = ({ className }) => (
  <svg className={className} viewBox="0 0 20 20" aria-hidden="true"><path d="M5 7.5l5 5 5-5" /></svg>
);

const CourseItem = ({ c, onPick }) => (
  <li>
    <a className="sh-item" href={c.link} onClick={onPick}>
      <img
        className="sh-item__img"
        src={c.image}
        alt=""
        width="44"
        height="44"
        loading="lazy"
        onError={(e) => { e.currentTarget.style.visibility = "hidden"; }}
      />
      <span className="sh-item__txt">
        <span className="sh-item__name">
          {c.name}
          {c.badge && <span className="sh-item__badge">{c.badge}</span>}
        </span>
        <span className="sh-item__desc">{c.description}</span>
      </span>
    </a>
  </li>
);

const SiteHeader = () => {
  const [megaOpen, setMegaOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [active, setActive] = useState(CATEGORIES[0]);
  const [scrolled, setScrolled] = useState(false);
  const rootRef = useRef(null);
  const hoverTimer = useRef(null);

  const closeAll = useCallback(() => {
    setMegaOpen(false);
    setMenuOpen(false);
  }, []);

  // Escape / outside click close whichever panel is open.
  useEffect(() => {
    if (!megaOpen && !menuOpen) return;
    const onKey = (e) => e.key === "Escape" && closeAll();
    const onDown = (e) => rootRef.current && !rootRef.current.contains(e.target) && closeAll();
    document.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onDown);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("mousedown", onDown);
    };
  }, [megaOpen, menuOpen, closeAll]);

  // "Log In" shows only at the very top; once the page scrolls it collapses
  // and "Request a Call" grows into the freed space.
  useEffect(() => {
    let ticking = false;
    const update = () => {
      ticking = false;
      setScrolled(window.scrollY > 2);
    };
    const onScroll = () => {
      if (!ticking) {
        ticking = true;
        window.requestAnimationFrame(update);
      }
    };
    update();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Lock page scroll behind the mobile menu.
  useEffect(() => {
    if (!menuOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    document.body.classList.add("sh-menu-open");
    return () => {
      document.body.style.overflow = prev;
      document.body.classList.remove("sh-menu-open");
    };
  }, [menuOpen]);

  // Hover opens the mega menu on pointer devices; click/keyboard always works.
  const hoverOpen = () => {
    clearTimeout(hoverTimer.current);
    if (window.matchMedia("(hover: hover)").matches) setMegaOpen(true);
  };
  const hoverClose = () => {
    clearTimeout(hoverTimer.current);
    hoverTimer.current = setTimeout(() => setMegaOpen(false), 180);
  };

  return (
    <div className={`sh${scrolled ? " sh--scrolled" : ""}`} ref={rootRef}>
      <header className="sh-bar">
        <div className="sh-inner">
          <a className="sh-brand" href="index.html" aria-label="LogicMojo home">
            <img src={LOGO} alt="LogicMojo" width="44" height="44" />
            <span className="sh-brand__text">
              <span className="sh-brand__title">AI &amp; ML Course</span>
              <span className="sh-brand__badge">New Batch · 14th Nov</span>
            </span>
          </a>

          <nav className="sh-nav" aria-label="Main">
            <div className="sh-dd" onMouseEnter={hoverOpen} onMouseLeave={hoverClose}>
              <button
                type="button"
                className="sh-link sh-dd__btn"
                aria-expanded={megaOpen}
                aria-controls="sh-mega"
                onClick={() => setMegaOpen((o) => !o)}
              >
                Courses <Chevron className="sh-chev" />
              </button>

              {megaOpen && (
                <div className="sh-mega" id="sh-mega">
                  <div className="sh-mega__rail" role="tablist" aria-label="Course categories">
                    {CATEGORIES.map((cat) => (
                      <button
                        key={cat}
                        type="button"
                        role="tab"
                        aria-selected={active === cat}
                        className="sh-rail__btn"
                        onClick={() => setActive(cat)}
                        onMouseEnter={() => setActive(cat)}
                        onFocus={() => setActive(cat)}
                      >
                        <i className={`fa ${categoryMeta[cat]?.icon || "fa-book"}`} aria-hidden="true" />
                        <span>{cat}</span>
                        <span className="sh-rail__count">{courseCategories[cat].length}</span>
                      </button>
                    ))}
                  </div>
                  <div className="sh-mega__body" role="tabpanel" aria-label={active}>
                    <p className="sh-mega__head">{active}</p>
                    <ul className={`sh-mega__list ${courseCategories[active].length > 4 ? "is-two-col" : ""}`}>
                      {courseCategories[active].map((c) => (
                        <CourseItem c={c} key={c.name + c.link} onPick={closeAll} />
                      ))}
                    </ul>
                    <div className="sh-mega__foot">
                      <span>Not sure which course fits you?</span>
                      <a href={CALENDLY_URL} target="_blank" rel="noopener noreferrer" onClick={closeAll}>
                        Talk to a course advisor →
                      </a>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {NAV_LINKS.map((l) => (
              <a key={l.href} className="sh-link" href={l.href}>{l.label}</a>
            ))}
            <a className="sh-link" href={BROCHURE_URL} target="_blank" rel="noopener noreferrer">Brochure</a>
          </nav>

          <div className="sh-actions">
            <a className="sh-login" href={LOGIN_URL} target="_default" aria-hidden={scrolled} tabIndex={scrolled ? -1 : undefined}>Log In</a>
            <a className="sh-cta" href={CALENDLY_URL} target="_blank" rel="noopener noreferrer">
              <i className="fa fa-phone" aria-hidden="true" />
              <span>Request a Call</span>
            </a>
            <button
              type="button"
              className="sh-burger"
              aria-expanded={menuOpen}
              aria-controls="sh-mobile"
              aria-label={menuOpen ? "Close menu" : "Open menu"}
              onClick={() => setMenuOpen((o) => !o)}
            >
              <span /><span /><span />
            </button>
          </div>
        </div>

        {menuOpen && (
          <div className="sh-mobile" id="sh-mobile">
            <nav aria-label="Mobile">
              <p className="sh-mobile__label">Courses</p>
              {CATEGORIES.map((cat, i) => (
                <details className="sh-acc" key={cat} open={i === 0}>
                  <summary>
                    <i className={`fa ${categoryMeta[cat]?.icon || "fa-book"}`} aria-hidden="true" />
                    <span>{cat}</span>
                    <span className="sh-rail__count">{courseCategories[cat].length}</span>
                    <Chevron className="sh-chev" />
                  </summary>
                  <ul>
                    {courseCategories[cat].map((c) => (
                      <CourseItem c={c} key={c.name + c.link} onPick={closeAll} />
                    ))}
                  </ul>
                </details>
              ))}

              <p className="sh-mobile__label">Explore</p>
              <ul className="sh-mobile__links">
                {NAV_LINKS.map((l) => (
                  <li key={l.href}><a href={l.href} onClick={closeAll}>{l.label}</a></li>
                ))}
                <li><a href={BROCHURE_URL} target="_blank" rel="noopener noreferrer" onClick={closeAll}>Course Brochure</a></li>
                <li><a href={LOGIN_URL} target="_default" onClick={closeAll}>Log In</a></li>
              </ul>
            </nav>
            <div className="sh-mobile__cta">
              <a className="sh-cta" href={CALENDLY_URL} target="_blank" rel="noopener noreferrer" onClick={closeAll}>
                <i className="fa fa-phone" aria-hidden="true" /> Request a Call
              </a>
              <a className="sh-wa" href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" onClick={closeAll}>
                <i className="fa fa-whatsapp" aria-hidden="true" /> WhatsApp
              </a>
            </div>
          </div>
        )}
      </header>
      {menuOpen && <div className="sh-overlay" onClick={closeAll} aria-hidden="true" />}
      {/* Reserves room for the fixed bar so page content starts below it */}
      <div className="sh-spacer" aria-hidden="true" />
    </div>
  );
};

export default SiteHeader;
